/**
 * MentionPicker — relay-66 inline `@` mention picker for the composer.
 *
 * Renamed from `ERMentionMenu.tsx` (EFISSUE-830). Same component, new
 * filename per planner directive seq 679 file list. Behavior unchanged:
 * filters workspace participants by `query`, supports keyboard
 * navigation (↑/↓/Enter/Tab/Escape), and calls `onSelect(name)` on
 * pick. Visual treatment matches `TagPicker` so both pickers feel
 * identical.
 *
 * Selection contract: the composer strips the `@query` prefix from the
 * textarea body (relay-66 behavior change — was `@name ` insertion) and
 * adds the `agent:<name>` tag to its tag-chip array.
 */

import { useEffect, useMemo, useState } from "react";

interface Props {
  /** All workspace participants currently active. */
  participants: string[];
  /** Current query string (what the user has typed after `@`). */
  query: string;
  onSelect: (name: string) => void;
  onClose: () => void;
}

export function MentionPicker({ participants, query, onSelect, onClose }: Props) {
  const filtered = useMemo(
    () => participants.filter((p) => p.toLowerCase().startsWith(query.toLowerCase())),
    [participants, query],
  );
  const [highlightIdx, setHighlightIdx] = useState(0);

  useEffect(() => {
    setHighlightIdx((prev) => (filtered.length === 0 ? 0 : Math.min(prev, filtered.length - 1)));
  }, [filtered.length]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (filtered.length === 0 && e.key !== "Escape") return;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        e.stopPropagation();
        setHighlightIdx((i) => (i + 1) % filtered.length);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        e.stopPropagation();
        setHighlightIdx((i) => (i - 1 + filtered.length) % filtered.length);
      } else if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        e.stopPropagation();
        const pick = filtered[highlightIdx];
        if (pick) onSelect(pick);
      } else if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }
    }
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [filtered, highlightIdx, onSelect, onClose]);

  if (filtered.length === 0) return null;

  return (
    <div
      role="listbox"
      aria-label="Mention a participant"
      style={{
        position: "absolute",
        bottom: "calc(100% + 4px)",
        left: 0,
        zIndex: 11,
        background: "var(--surface)",
        border: "1px solid var(--border)",
        borderRadius: 6,
        padding: 4,
        minWidth: 180,
        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
      }}
    >
      {filtered.map((name, i) => {
        const isActive = i === highlightIdx;
        return (
          <button
            key={name}
            type="button"
            role="option"
            aria-selected={isActive}
            onMouseEnter={() => setHighlightIdx(i)}
            onClick={() => onSelect(name)}
            style={{
              display: "block",
              width: "100%",
              textAlign: "left",
              background: isActive ? "var(--surface2)" : "transparent",
              color: "var(--text)",
              border: "none",
              padding: "6px 10px",
              fontSize: 12,
              fontFamily: "var(--font-mono)",
              cursor: "pointer",
              borderRadius: 4,
            }}
          >
            @{name}
          </button>
        );
      })}
    </div>
  );
}
